import { useEffect, useRef, useState } from 'react'
import { authApiService } from '../services/authApiService'

/**
 * Cloudflare Turnstile for the public auth forms, driven entirely by the server's `Turnstile` feature
 * flag: `useTurnstileConfig` asks the API whether the flag is on and for the public site key, so
 * flipping the appsettings flag turns both the widget and the server-side enforcement on or off
 * together. The secret key never reaches the browser.
 */

interface TurnstileConfig { enabled: boolean; siteKey: string; loading: boolean }

// One shared fetch across every auth page in a session.
let configPromise: Promise<{ enabled: boolean; siteKey: string }> | null = null
function loadConfig() {
  configPromise ??= authApiService.turnstileConfig().catch(() => ({ enabled: false, siteKey: '' }))
  return configPromise
}

export function useTurnstileConfig(): TurnstileConfig {
  const [cfg, setCfg] = useState<TurnstileConfig>({ enabled: false, siteKey: '', loading: true })
  useEffect(() => {
    let cancelled = false
    loadConfig().then(c => { if (!cancelled) setCfg({ ...c, loading: false }) })
    return () => { cancelled = true }
  }, [])
  return cfg
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function ensureScript(): Promise<void> {
  if ((window as { turnstile?: unknown }).turnstile) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) { existing.addEventListener('load', () => resolve()); existing.addEventListener('error', reject); return }
    const s = document.createElement('script')
    s.src = SCRIPT_SRC; s.async = true; s.defer = true
    s.onload = () => resolve()
    s.onerror = reject
    document.head.appendChild(s)
  })
}

/**
 * Renders the widget (invisible unless Cloudflare asks for interaction) and reports the token: a
 * non-empty string once solved, and '' when it expires or errors so the parent can re-disable submit.
 * If the script cannot load, it says so rather than leaving submit silently disabled.
 */
export function TurnstileWidget({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const [interactive, setInteractive] = useState(false)

  useEffect(() => {
    let widgetId: string | undefined
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ts = () => (window as any).turnstile

    ensureScript().then(() => {
      if (cancelled || !ref.current || !ts()) return
      widgetId = ts().render(ref.current, {
        sitekey: siteKey,
        // Hidden unless Cloudflare needs the person to interact; most visitors never see it.
        appearance: 'interaction-only',
        'before-interactive-callback': () => setInteractive(true),
        'after-interactive-callback': () => setInteractive(false),
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      })
    }).catch(() => { if (!cancelled) setFailed(true) })

    return () => {
      cancelled = true
      try { if (widgetId && ts()) ts().remove(widgetId) } catch { /* ignore */ }
    }
  }, [siteKey, onToken])

  return (
    <div style={{ marginBottom: interactive || failed ? 16 : 0 }}>
      <div ref={ref} />
      {failed && (
        <p role="alert" style={{ color: '#b42318', fontSize: 13, margin: 0 }}>
          The verification check couldn't load, so this form can't be submitted. Reload the page, or allow
          challenges.cloudflare.com if a browser extension blocks it.
        </p>
      )}
    </div>
  )
}
